import type { ReactNode } from 'react';

import { STATUS_COLORS, type ExamSlice } from './examResults';

const DONUT_RADIUS = 30;
const DONUT_BOX = 76;
const DONUT_CENTRE = DONUT_BOX / 2;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Hand-rolled multi-slice doughnut; the repo carries no chart library. */
function Donut({ slices }: { slices: ExamSlice[] }) {
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  const drawn = total > 0 ? slices.filter((slice) => slice.count > 0) : [{ title: 'Chưa có dữ liệu', count: 1, color: STATUS_COLORS.idle }];
  const span = total > 0 ? total : 1;
  let consumed = 0;
  return (
    <svg className="quiz-results__donut-svg" width={DONUT_BOX} height={DONUT_BOX} viewBox={`0 0 ${DONUT_BOX} ${DONUT_BOX}`} aria-hidden={true}>
      {drawn.map((slice) => {
        const length = (DONUT_CIRCUMFERENCE * slice.count) / span;
        const offset = consumed;
        consumed += length;
        return <circle key={slice.title} cx={DONUT_CENTRE} cy={DONUT_CENTRE} r={DONUT_RADIUS} fill="none" stroke={slice.color} strokeWidth="9" strokeDasharray={`${length} ${DONUT_CIRCUMFERENCE - length}`} strokeDashoffset={-offset} transform={`rotate(-90 ${DONUT_CENTRE} ${DONUT_CENTRE})`} />;
      })}
    </svg>
  );
}

/** `centre` sits in the ring's hole, e.g. the average score. */
export function DonutRow({ slices, centre }: { slices: ExamSlice[]; centre?: ReactNode }) {
  return (
    <div className="quiz-results__donut-row">
      <div className="quiz-results__donut"><Donut slices={slices} />{centre ? <div className="quiz-results__donut-centre">{centre}</div> : null}</div>
      <ul className="quiz-results__legend">
        {slices.filter((slice) => slice.count > 0).map((slice) => (
          <li key={slice.title} className="quiz-results__legend-row">
            <span className="quiz-results__legend-dot" style={{ background: slice.color }} />
            <span className="quiz-results__legend-title">{slice.title}</span>
            <span className="quiz-results__legend-count">{slice.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function share(count: number, total: number): string {
  return `${total > 0 ? Math.round((count / total) * 1000) / 10 : 0}%`;
}

/** "4 học sinh / 5" over a meter; `status` stacks Đã nộp, Đang làm, Chưa làm with a legend. */
export function SubmittedMeter({ submitted, total, status }: { submitted: number; total: number; status?: ExamSlice[] }) {
  const parts = status || [{ title: 'Đã nộp', count: submitted, color: STATUS_COLORS.done }];
  return (
    <section className="quiz-results__block">
      <h3 className="quiz-results__block-title">Số học sinh đã nộp</h3>
      <p className="quiz-results__stat">
        <span className="quiz-results__stat-value">{submitted}</span>
        <span className="quiz-results__stat-total"> / {total} học sinh</span>
      </p>
      <div className="quiz-results__meter" role="img" aria-label={parts.map((part) => `${part.title}: ${part.count}`).join(', ')}>
        {parts.map((part) => (part.count > 0 ? <span key={part.title} className="quiz-results__meter-fill" style={{ width: share(part.count, total), background: part.color }} /> : null))}
      </div>
      {status ? (
        <ul className="quiz-results__meter-legend">
          {status.map((part) => (
            <li key={part.title}><span className="quiz-results__legend-dot" style={{ background: part.color }} />{part.title} <strong>{part.count}</strong></li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
